import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/platform/store/types';
import { analytics } from '@/shared/services/analytics.service';
import { useTheme } from '@/theme/useTheme';

/**
 * The single owner of analytics identity. Mounted once by `bootstrap` under
 * `PersistGate` and `ThemeProvider`, renders nothing.
 *
 * - user present (login or a rehydrated session) → `identify` with the person
 *   properties and `organization_id` as a super property, plus Sentry's user;
 * - user gone after having been present → `reset` (a new anonymous visitor);
 * - theme / language → super properties on every event, `settings:theme_changed`
 *   after the first paint.
 *
 * Plan and subscription properties are not read here: the billing feature sets
 * them on the person when it loads or changes the subscription, and the backend
 * sets them on its `subscription:*` facts, so the platform never imports a
 * feature slice.
 */
export const AnalyticsSession = () => {
  const user = useAppSelector((state) => state.auth.user);
  const { theme, preference } = useTheme();
  const { i18n } = useTranslation();
  const language = i18n.language;

  // Deliberately not reset in an effect cleanup: StrictMode runs
  // mount → unmount → mount and would flap the identity on every load.
  const identifiedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = user?._id;
    if (id) {
      if (identifiedIdRef.current === id) return;
      identifiedIdRef.current = id;
      analytics.identify(
        id,
        {
          email: user.email,
          name: [user.name, user.last_name].filter(Boolean).join(' ') || undefined,
          login_provider: user.login_provider,
          type_user: user.type_user,
          register_status: user.register_status,
          organization_id: user.organization_id,
          is_internal: user.type_user === 'internal_user',
        },
        {
          signup_date: user.created_date
            ? new Date(user.created_date).toISOString()
            : undefined,
        },
      );
      analytics.register({ organization_id: user.organization_id });
      analytics.setPersonPropertiesForFlags({
        organization_id: user.organization_id,
        type_user: user.type_user,
      });
      Sentry.setUser({ id });
      return;
    }
    if (identifiedIdRef.current) {
      identifiedIdRef.current = null;
      analytics.reset();
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    analytics.register({ language });
  }, [language]);

  const themeMountedRef = useRef(false);
  useEffect(() => {
    analytics.register({ theme });
    if (!themeMountedRef.current) {
      themeMountedRef.current = true;
      return;
    }
    analytics.capture('settings:theme_changed', { theme, preference });
  }, [theme, preference]);

  return null;
};
